import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { serverTime } from '../utils/time';

/** /user/me response -> the user object the app keeps (and stores in localStorage). */
function toUserData(u) {
  const role = typeof u.role === 'string' ? u.role : u.role?.name || u.role;
  // The server only flips expired accounts on a 15-min schedule (SubscriptionScheduler),
  // so a just-passed end date from this fresh response already means "ended".
  const end = serverTime(u.subscriptionEndDate);
  const staff = role === 'ROLE_ADMIN' || role === 'ROLE_SEMI_ADMIN';
  const ended = !staff && !!end && !Number.isNaN(end.getTime()) && end.getTime() <= Date.now();
  return {
    id: u.id,
    email: u.email,
    phoneNumber: u.phoneNumber,
    fullName: u.fullName,
    role,
    subscriptionActive: ended ? false : u.subscriptionActive,
    freeTrialUsed: u.freeTrialUsed,
    createdAt: u.createdAt,
    subscriptionEndDate: u.subscriptionEndDate,
    needsPayment: ended ? true : !!u.needsPayment,
  };
}

/** How often an open app re-checks the subscription with the server. */
const SUBSCRIPTION_RECHECK_MS = 5 * 60 * 1000;

const AuthContext = createContext(null);

function storeSession(data) {
  localStorage.setItem('token', data.token);
  const userData = {
    id: data.id,
    email: data.email,
    phoneNumber: data.phoneNumber,
    fullName: data.fullName,
    role: data.role,
    subscriptionActive: data.subscriptionActive,
    freeTrialUsed: data.freeTrialUsed,
    createdAt: data.createdAt,
    subscriptionEndDate: data.subscriptionEndDate,
    needsPayment: !!data.needsPayment,
  };
  localStorage.setItem('user', JSON.stringify(userData));
  return userData;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    if (stored && token) {
      try { setUser(JSON.parse(stored)); } catch (_) {}
      // Refresh from server so expired 7-day trial flips to needsPayment
      api.get('/user/me')
        .then((res) => {
          const userData = toUserData(res.data);
          localStorage.setItem('user', JSON.stringify(userData));
          setUser(userData);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
      return;
    }
    setLoading(false);
  }, []);

  const applyAuth = (data) => {
    const userData = storeSession(data);
    setUser(userData);
    return userData;
  };

  const loginWithCredentials = async (emailOrPhone, password) => {
    const res = await api.post('/auth/login', { emailOrPhone, password });
    return applyAuth(res.data);
  };

  const register = async (payload) => {
    const res = await api.post('/auth/register', payload);
    return applyAuth(res.data);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const refreshUser = useCallback(async () => {
    try {
      const res = await api.get('/user/me');
      const userData = toUserData(res.data);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      return userData;
    } catch (_) {
      return null;
    }
  }, []);

  // A phone app can stay open / in the background for days, so the one check at
  // startup isn't enough: re-check when the app comes back to the foreground, every
  // few minutes while open, and right when the subscription is due to end. Once it
  // has ended, PrivateRoute (App.jsx) sends the user to the Subscribe/Plan page.
  const isStaff = user?.role === 'ROLE_ADMIN' || user?.role === 'ROLE_SEMI_ADMIN';
  const loggedInUser = !!user && !isStaff;
  const watchSubscription = loggedInUser && user.subscriptionActive !== false && !user.needsPayment;
  const endDate = user?.subscriptionEndDate;

  // Every regular user, paid or not: re-check when the app comes back to the
  // foreground — also how a plan just bought in the phone's browser (activated by the
  // Stripe webhook) shows up the moment the user returns to the app.
  useEffect(() => {
    if (!loggedInUser) return undefined;
    const onVisible = () => { if (document.visibilityState === 'visible') refreshUser(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [loggedInUser, refreshUser]);

  useEffect(() => {
    if (!watchSubscription) return undefined;
    const interval = setInterval(refreshUser, SUBSCRIPTION_RECHECK_MS);

    let endTimer;
    const end = serverTime(endDate);
    if (end && !Number.isNaN(end.getTime())) {
      const wait = end.getTime() - Date.now() + 60 * 1000; // 1 min after the end, server has flipped it
      // setTimeout can't wait longer than ~24.8 days; the interval covers anything further out.
      if (wait > 0 && wait < 2 ** 31 - 1) endTimer = setTimeout(refreshUser, wait);
    }
    return () => {
      clearInterval(interval);
      if (endTimer) clearTimeout(endTimer);
    };
  }, [watchSubscription, endDate, refreshUser]);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      applyAuth,
      login: loginWithCredentials,
      loginWithCredentials,
      register,
      logout,
      refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
